"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ShieldAlert, PhoneCall, CircleCheck as CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const protocolSteps = [{
  step: "01",
  title: "Critical barrier isolation",
  description: "Negative air staging and physical barriers prevent airborne microbes from reaching unaffected living areas."
}, {
  step: "02",
  title: "Heavy liquid & solids extraction",
  description: "Commercial truck-mounted vacuum extractors pull gross contamination and standing black water immediately."
}, {
  step: "03",
  title: "Compromised porous removal",
  description: "Saturated drywall, baseboards, and carpet padding are cleanly cut and safely disposed of per EPA guidelines."
}, {
  step: "04",
  title: "Hospital-grade antimicrobial wash",
  description: "EPA-registered botanical and industrial biocides sanitize subfloors, studs, and hard surfaces against pathogens."
}, {
  step: "05",
  title: "Industrial dehumidification & heat drying",
  description: "Commercial desiccant dehumidifiers and air movers draw deep moisture out of structural framing."
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const entranceVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4 mb-10 text-xs font-mono text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-destructive animate-pulse" />
            <span className="font-semibold text-foreground uppercase tracking-wider">Hazard Protocol: Category 3 Black Water</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Direct Local Crew Dispatch</span>
            <span className="hidden sm:inline text-border">|</span>
            <span className="hidden sm:inline">MD • DC • Northern VA</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          {/* Left Column: Context, Hazard Explanation, and Protocol */}
          <motion.div className="lg:col-span-7 flex flex-col space-y-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={entranceVariant}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-destructive/10 text-destructive border border-destructive/20 text-xs font-bold uppercase tracking-wider mb-4">
                <AlertTriangle className="size-3.5" />
                <span>Immediate Biohazard Assessment</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground leading-tight">
                Category 3 sewage carries biological hazards that require immediate containment
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Municipal main backups, tree-root line compromises, and storm drain overflows do not just bring dirty water into your home or facility. They introduce gross biological contamination—classified by the IICRC as Category 3 black water—packed with harmful bacteria, viruses, and parasites.
              </p>
            </div>

            {/* Pathogen & Risk Warning Card */}
            <div className="p-5 sm:p-6 rounded-lg bg-muted/70 border border-border space-y-3">
              <div className="flex items-center gap-2.5 text-foreground font-semibold text-base">
                <ShieldAlert className="size-5 text-destructive" />
                <h3>The risk of uncontained sewage in interior spaces</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Contact with black water poses acute health hazards through skin contact and airborne aerosolization. Porous materials like drywall, baseboards, and carpet padding wick contaminated water upward, breeding secondary toxic mold colonies within 24 to 48 hours if not extracted and sanitized by certified crews.
              </p>
            </div>

            {/* Decontamination Sequence */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold tracking-tight text-foreground">
                  Our systematic decontamination protocol
                </h3>
                <span className="text-xs font-mono text-primary font-semibold uppercase tracking-wider">
                  Direct Field Technicians
                </span>
              </div>

              <div className="space-y-3">
                {protocolSteps.map((item, i) => <div key={item.step} data-index={i} className="p-4 rounded-md border border-border bg-card transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-xs flex gap-4">
                    <div className="font-mono text-sm font-bold text-primary shrink-0 pt-0.5">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {item.title}
                      </h4>
                      <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-normal">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Action Triggers */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                <a href="tel:855-435-6631" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call 855-435-6631 for 24/7 Dispatch</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium">
                <Link to="/ContactRequestServicePage" className="flex items-center justify-center gap-2">
                  <span>Schedule Site Assessment</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Equipment Photo & Verification Card */}
          <motion.div className="lg:col-span-5 flex flex-col space-y-6" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} variants={entranceVariant}>
            {/* Documentary Staged Equipment Frame */}
            <div className="relative rounded-lg border border-border bg-muted overflow-hidden shadow-sm">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_decontamination_gear.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_decontamination_gear.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_decontamination_gear.png-wvc-sizes" alt="Industrial HEPA air scrubbers, protective Tyvek suits, and hospital-grade decontamination solutions staged for sewage extraction" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs text-white">
                  <span className="font-mono bg-black/60 px-2.5 py-1 rounded border border-white/20 backdrop-blur-xs">
                    HEPA Air Scrubbing + Antimicrobial Stage
                  </span>
                  <span className="font-semibold text-accent flex items-center gap-1">
                    <Sparkles className="size-3.5" /> Direct On-Site Crew
                  </span>
                </div>
              </div>

              {/* Direct Field Credential Box */}
              <div className="p-5 bg-card border-t border-border space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-border">
                  <span className="text-xs font-mono uppercase text-muted-foreground">Response Capability</span>
                  <span className="text-xs font-semibold text-primary">Zero Outsourcing</span>
                </div>

                <ul className="space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Certified technicians equipped with full PPE and containment systems</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Real-time photo logs and moisture readings provided directly to your insurer</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>Complete rebuilding services to restore drywall, flooring, and trim post-sanitization</span>
                  </li>
                </ul>

                <div className="pt-2 rounded bg-muted/60 p-3 text-xs text-muted-foreground border border-border/80">
                  <span className="font-semibold text-foreground">Insurance Billing Notice:</span> We document every stage of Category 3 loss using industry-standard pricing schedules, coordinating directly with your adjuster to streamline claims.
                </div>
              </div>
            </div>

            {/* Quick Regional Verification Box */}
            <div className="p-4 rounded-md border border-border bg-card flex items-center justify-between text-xs">
              <div>
                <p className="font-semibold text-foreground">Mid-Atlantic Response Zone</p>
                <p className="text-muted-foreground mt-0.5">Maryland • Washington DC • Northern Virginia</p>
              </div>
              <span className="font-mono font-bold text-primary bg-primary/10 px-2.5 py-1 rounded border border-primary/20">
                24/7 Live
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}